import React, { useState } from 'react';
import { X, Loader2, FileText, Send, Edit3, Wand2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from './constants';
import { PitchEmailModal } from './PitchEmailModal';

export const PitchGenerator = ({ roleId, roleTitle, company, candidateKeys, candidateNames, onClose, onPitchReady }) => {
  const [pitchType, setPitchType] = useState('short');
  const [loading, setLoading] = useState(false);
  const [pitchText, setPitchText] = useState('');
  const [generated, setGenerated] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/generate-pitch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role_id: roleId, candidate_keys: candidateKeys, pitch_type: pitchType }),
      });
      const data = await res.json();
      if (data.error) { toast.error(data.detail); }
      else { setPitchText(data.pitch_text); setGenerated(true); }
    } catch { toast.error('Pitch generation failed'); }
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(pitchText);
    toast.success('Pitch copied to clipboard');
  };

  const handleSubmit = () => {
    if (onPitchReady) onPitchReady(pitchText, pitchType);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="pitch-generator-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-gradient-to-br from-[#D4AF37] to-[#c5a030] rounded-lg flex items-center justify-center">
              <Wand2 className="h-4 w-4 text-[#0A1F44]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0A1F44]">Generate Client Pitch</h2>
              <p className="text-[10px] text-gray-500">{roleTitle} at {company} — {candidateKeys.length} candidate{candidateKeys.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        {!generated ? (
          <div className="p-6 space-y-4">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-2">Candidates</p>
              <div className="flex flex-wrap gap-1.5">
                {candidateNames.map((n, i) => (
                  <span key={i} className="px-2 py-1 bg-[#0A1F44]/5 text-[#0A1F44] text-xs font-medium rounded-lg">{n}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-2">Pitch Style</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setPitchType('short')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${pitchType === 'short' ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="pitch-type-short">
                  <p className="text-sm font-bold text-[#0A1F44]">Short Pitch</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">Quick send. Concise intro + bullet points. Ideal for warm contacts.</p>
                </button>
                <button onClick={() => setPitchType('detailed')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${pitchType === 'detailed' ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="pitch-type-detailed">
                  <p className="text-sm font-bold text-[#0A1F44]">Detailed Pitch</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">High-touch. Full candidate profiles + detailed strengths. Ideal for cold outreach.</p>
                </button>
              </div>
            </div>
            <button onClick={generate} disabled={loading}
              className="w-full px-4 py-3 bg-[#D4AF37] text-[#0A1F44] text-sm font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="generate-pitch-btn">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating with AI...</> : <><Wand2 className="h-4 w-4" /> Generate {pitchType === 'short' ? 'Short' : 'Detailed'} Pitch</>}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#0A1F44] text-[10px] font-bold rounded">AI Generated</span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded capitalize">{pitchType} pitch</span>
                </div>
                <button onClick={() => setGenerated(false)} className="text-xs text-gray-500 hover:text-[#0A1F44] flex items-center gap-1">
                  <Edit3 className="h-3 w-3" /> Change style
                </button>
              </div>
              <textarea value={pitchText} onChange={e => setPitchText(e.target.value)}
                className="w-full min-h-[300px] p-4 text-sm text-[#0A1F44] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 leading-relaxed resize-y font-sans"
                data-testid="pitch-text-editor" />
              <p className="text-[9px] text-gray-400 mt-1">You can edit the pitch above before copying or submitting.</p>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-xl">
              <button onClick={generate} disabled={loading}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 flex items-center gap-1"
                data-testid="regenerate-pitch-btn">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Regenerate
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleCopy}
                  className="px-4 py-1.5 text-xs font-bold text-[#0A1F44] border border-[#0A1F44]/20 rounded-lg hover:bg-[#0A1F44]/5 flex items-center gap-1"
                  data-testid="copy-pitch-btn">
                  <FileText className="h-3 w-3" /> Copy Pitch
                </button>
                <button onClick={() => setShowEmailModal(true)}
                  className="px-4 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 flex items-center gap-1"
                  data-testid="send-via-email-btn">
                  <Mail className="h-3 w-3" /> Send via Email
                </button>
                <button onClick={handleSubmit}
                  className="px-4 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 flex items-center gap-1"
                  data-testid="proceed-to-submit-btn">
                  <Send className="h-3 w-3" /> Submit to Client
                </button>
              </div>
            </div>
          </>
        )}

        {/* Send via Email Modal */}
        {showEmailModal && (
          <PitchEmailModal
            roleId={roleId}
            roleTitle={roleTitle}
            company={company}
            candidateKeys={candidateKeys}
            candidateNames={candidateNames}
            pitchText={pitchText}
            pitchType={pitchType}
            onClose={() => setShowEmailModal(false)}
            onSent={() => {
              setShowEmailModal(false);
              toast.success('Pitch sent and logged');
            }}
          />
        )}
      </div>
    </div>
  );
};
